import {Component, createRef, useEffect} from 'react';
import {Link, NavigateFunction, useNavigate, useParams} from 'react-router-dom';
import {AuthContextType, useAuth} from '../contexts/AuthContext';
import {CryptoHelper} from '../utils/cryptoHelper';
import {SignalRService} from '../utils/signalrService';
import {ClaudeClient, ContentDetailsDto, ContentsClient, TypingEventData, PresenceEventData} from '../api/api-client';
import {AiRequest} from '../services/AiRequest';
import type {WebSearchSource} from '../services/aiRequestTypes';
import {ErrorHandler} from "../utils/errorHandler";
import MessageComponent from './MessageComponent';
import {ThreadPermissions} from './ThreadPermissions';
import ScrollToBottom, { useScrollToBottom } from 'react-scroll-to-bottom';
import { ThreadProvider, ThreadContext } from '../contexts/ThreadContext';
import type { ThreadContextType } from '../contexts/ThreadContext';
import { useQueryClient, QueryClient } from '@tanstack/react-query';
import {DecryptedContentVersionDto, DecryptedThread} from "../hooks/useThread";
import {deleteThreadWithConfirmation, renameThreadWithConfirmation} from '../utils/threadActions';
import {UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem} from 'reactstrap';

interface ThreadDetailProps {
   id: number | undefined; // undefined = new thread.
   navigate: NavigateFunction;
   auth: AuthContextType;
   threadContext: ThreadContextType;
   queryClient: QueryClient;
}

interface ThreadDetailState {
   newMessage: string;
   sending: boolean;
   activeAiResponse: AiRequest | null;
   aiResponseContent: string;
   aiResponseThinking: string;
   aiResponseMessageId: number | null;
   aiResponseWebSearchSources: WebSearchSource[];
   showPermissions: boolean;
   typingUsers: { userId: string; userName: string }[];
   userPresence: Record<string, { isOnline: boolean; lastSeen: Date }>;
}

// Calculate cost based on Claude-4 Sonnet pricing
// Can be updated as pricing changes
function calculateCost(inputTokens: number, outputTokens: number): number {
   // Claude-4 Sonnet pricing: $15 per 1M input tokens, $75 per 1M output tokens
   const inputCost = (inputTokens / 1000000) * 15;
   const outputCost = (outputTokens / 1000000) * 75;
   return inputCost + outputCost;
}

// Minimal hook component to expose scrollToBottom to your class component
const ScrollController: React.FC<{ getScrollFn: (fn: () => void) => void }> = ({ getScrollFn }) => {
   const scrollToBottom = useScrollToBottom();

   useEffect(() => {
      getScrollFn(scrollToBottom);
   }, [scrollToBottom, getScrollFn]);

   return null;
};

class ThreadDetail extends Component<ThreadDetailProps, ThreadDetailState> {
   private autoSaveIntervalRef: NodeJS.Timeout | null = null;
   private pauseSaveTimeoutRef: NodeJS.Timeout | null = null;
   private lastSavedContentRef: string = '';
   private textareaRef = createRef<HTMLTextAreaElement>();
   private isCurrentlyTyping: boolean = false;

   constructor(props: ThreadDetailProps) {
      super(props);
      this.state = {
         newMessage: '',
         sending: false,
         activeAiResponse: null,
         aiResponseContent: '',
         aiResponseThinking: '',
         aiResponseMessageId: null,
         aiResponseWebSearchSources: [],
         showPermissions: false,
         typingUsers: [],
         userPresence: {}
      };
   }

   async componentDidMount() {
      this.setupSignalRListener();

      // Join the thread for real-time updates
      if (this.props.id) {
         await SignalRService.joinThread(this.props.id);
      }

      // Restore draft content if available on initial load
      const { threadContext } = this.props;
      if (threadContext.currentDraftContent) {
         this.setState({ newMessage: threadContext.currentDraftContent });
         this.lastSavedContentRef = threadContext.currentDraftContent;
         if (this.textareaRef.current) {
            this.textareaRef.current.value = threadContext.currentDraftContent;
         }
      }
   }

   componentWillUnmount() {
      this.cleanupTimers();
      this.cleanupSignalRListener();
      
      // Stop typing indicator if active
      if (this.props.id && this.isCurrentlyTyping) {
         this.isCurrentlyTyping = false;
         SignalRService.stopTyping(this.props.id);
      }
      
      // Leave the thread when component unmounts
      if (this.props.id) {
         SignalRService.leaveThread(this.props.id);
      }
   }





   componentDidUpdate(prevProps: ThreadDetailProps, prevState: ThreadDetailState) {

      // Get context data once for this update
      const { threadContext } = this.props;
      const prevContext = prevProps.threadContext;
      
      // Restore draft content when it becomes available
      if (prevContext.currentDraftContent !== threadContext.currentDraftContent && threadContext.currentDraftContent) {
         this.setState({ newMessage: threadContext.currentDraftContent });
         this.lastSavedContentRef = threadContext.currentDraftContent;
         if (this.textareaRef.current) {
            this.textareaRef.current.value = threadContext.currentDraftContent;
         }
      }

      if (prevProps.id !== this.props.id || prevProps.auth.masterKey !== this.props.auth.masterKey) {
         // Stop typing indicator for previous thread if active
         if (prevProps.id && this.isCurrentlyTyping) {
            this.isCurrentlyTyping = false;
            SignalRService.stopTyping(prevProps.id);
         }
         
         // Leave previous thread
         if (prevProps.id) {
            SignalRService.leaveThread(prevProps.id);
         }
         
         if (this.props.id) {
            // Join new thread - data will come from props
            SignalRService.joinThread(this.props.id);
         } else {
            this.setState({
               newMessage: ''
            });
         }
      }

      // Setup SignalR listener when contentKey changes through context
      if (prevContext.contentKey !== threadContext.contentKey) {
         this.setupSignalRListener();
      }
   }
   
   private handleUserStartedTyping = (data: TypingEventData) => {
      console.log('User started typing:', data.userId, data.threadId, this.props.auth.user?.id?.toString());
      if ((data.threadId === this.props.id) && (data.userId !== this.props.auth.user?.id?.toString())) {
         console.log('Adding user to typing list:', data.userId);
         this.setState(prevState => ({
            typingUsers: [
               ...prevState.typingUsers.filter(u => u.userId !== data.userId),
               { userId: data.userId!, userName: data.userName || data.userId! }
            ]
         }));
      }
   };

   private handleUserStoppedTyping = (data: TypingEventData) => {
      if (data.threadId === this.props.id) {
         this.setState(prevState => ({
            typingUsers: prevState.typingUsers.filter(u => u.userId !== data.userId)
         }));
      }
   };
   
   private handleUserPresenceChanged = (data: PresenceEventData) => {
      this.setState(prevState => ({
         userPresence: {
            ...prevState.userPresence,
            [data.userId!]: {
               isOnline: data.isOnline!,
               lastSeen: new Date(data.lastSeen!)
            }
         }
      }));
   };
   
   private userLeftThread = (data: TypingEventData) => {
      console.log('User left thread:', data.userName, data.threadId);
   }
   
   private userJoinedThread = (data: TypingEventData) => {
      console.log('User joined thread:', data.userName, data.threadId);
   }

   private setupSignalRListener() {
      const {id, threadContext} = this.props;
      const {contentKey} = threadContext;

      if (!id || !contentKey) return;

      // Clean up existing listener
      this.cleanupSignalRListener();

      // Add all SignalR listeners
      SignalRService.addListener('UserStartedTyping', this.handleUserStartedTyping);
      SignalRService.addListener('UserStoppedTyping', this.handleUserStoppedTyping);
      SignalRService.addListener('UserPresenceChanged', this.handleUserPresenceChanged);
      SignalRService.addListener('UserLeftThread', this.userLeftThread);
      SignalRService.addListener('UserJoinedThread', this.userJoinedThread);
   }

   private cleanupSignalRListener() {
      SignalRService.removeListener('UserStartedTyping', this.handleUserStartedTyping);
      SignalRService.removeListener('UserStoppedTyping', this.handleUserStoppedTyping);
      SignalRService.removeListener('UserPresenceChanged', this.handleUserPresenceChanged);
      SignalRService.removeListener('UserLeftThread', this.userLeftThread);
      SignalRService.removeListener('UserJoinedThread', this.userJoinedThread);
   }


   // Save draft if content has changed
   private async saveIfChanged(content: string) {
      if (content === this.lastSavedContentRef) return;
      const { threadContext } = this.props;
      if (!threadContext.thread) return;

      const result = await threadContext.saveDraft(
         threadContext.currentDraftId ?? undefined,
         content);
      if (result?.id) {
         // Draft ID is managed by context, no need to update state
         this.lastSavedContentRef = content;
      } else if (!content.trim() && threadContext.currentDraftId) {
         // Draft was deleted due to empty content - context will handle this
         this.lastSavedContentRef = '';
      }
   }

   // Start auto-save interval when user is typing
   private startAutoSave() {
      // Clear any existing interval
      if (this.autoSaveIntervalRef) {
         clearInterval(this.autoSaveIntervalRef);
      }

      // Start interval to save every 3 seconds
      this.autoSaveIntervalRef = setInterval(() => {
         this.saveIfChanged(this.state.newMessage);
      }, 3000);
   }

   // Stop auto-save interval
   private stopAutoSave() {
      if (this.autoSaveIntervalRef) {
         clearInterval(this.autoSaveIntervalRef);
         this.autoSaveIntervalRef = null;
      }
   }

   // Handle message change with optimized auto-save
   private handleMessageChange = (value: string) => {
      this.setState({newMessage: value});

      // Clear existing pause timeout
      if (this.pauseSaveTimeoutRef) {
         clearTimeout(this.pauseSaveTimeoutRef);
      }

      // Send typing indicator only when starting to type
      if (this.props.id && value.trim() && !this.isCurrentlyTyping) {
         this.isCurrentlyTyping = true;
         SignalRService.startTyping(this.props.id);
      }

      // Only start interval-based saving once, not on every keystroke
      if (value.trim() && !this.autoSaveIntervalRef) {
         this.startAutoSave();
      } else if (!value.trim()) {
         this.stopAutoSave();
      }

      // Set timeout to save when user stops typing (increased to 3 seconds for less churn)
      this.pauseSaveTimeoutRef = setTimeout(async () => {
         await this.saveIfChanged(value); // Use the value directly to avoid stale state
         if (!value.trim()) {
            this.stopAutoSave(); // Stop interval if content is empty
         }
         
         // Stop typing indicator after pause
         if (this.props.id && this.isCurrentlyTyping) {
            this.isCurrentlyTyping = false;
            SignalRService.stopTyping(this.props.id);
         }
      }, 2500);
   }

   // Cleanup timers on unmount
   private cleanupTimers() {
      if (this.autoSaveIntervalRef) {
         clearInterval(this.autoSaveIntervalRef);
         this.autoSaveIntervalRef = null;
      }
      if (this.pauseSaveTimeoutRef) {
         clearTimeout(this.pauseSaveTimeoutRef);
         this.pauseSaveTimeoutRef = null;
      }
   }

   // Clear draft state and stop auto-save
   private clearDraftState() {
      // Cancel any pending auto-save
      this.cleanupTimers();

      // Stop typing indicator if active
      if (this.props.id && this.isCurrentlyTyping) {
         this.isCurrentlyTyping = false;
         SignalRService.stopTyping(this.props.id);
      }

      // Clear state and input
      this.setState({
         newMessage: ''
      });
      if (this.textareaRef.current) {
         this.textareaRef.current.value = '';
      }
      this.lastSavedContentRef = '';
   }

   // Check if we can ask AI about the previous message
   private canAskAboutPreviousMessage(): boolean {
      const { threadContext } = this.props;
      const messages = threadContext.messages || [];
      if (messages.length === 0) return false;
      
      // Can ask if the last message was from a user (not AI)
      const lastMessage = messages[messages.length - 1];
      return lastMessage.messageType === 'user';
   }

   // Get placeholder text based on current state
   private getPlaceholderText(): string {
      const { newMessage } = this.state;
      const { isAiMode } = this.props.threadContext;
      const canAskAboutPrevious = this.canAskAboutPreviousMessage();
      
      if (newMessage.trim() === '' && canAskAboutPrevious && isAiMode) {
         return "Ask AI about previous message, or type your own question...";
      } else if (isAiMode) {
         return "Type your question for AI...";
      } else {
         return "Type your message...";
      }
   }

   // Check if we can send a message (either has text or can ask about previous in AI mode)
   private canSendMessage(): boolean {
      const { newMessage } = this.state;
      
      // Can send if we have text content
      if (newMessage.trim()) {
         return true;
      }
      
      // Can send empty message if we can ask about previous (either in AI mode or direct Ask AI)
      return this.canAskAboutPreviousMessage();
   }

   private handleDeleteMessage = async (messageId: number) => {
      const { threadContext, auth } = this.props;

      if (!threadContext.contentKey || !threadContext.thread?.id || !auth.masterKey) return;

      // Use context method for deleting message
      await threadContext.deleteMessage(messageId);
   }

   // Strip metadata from content - return both the content with it stripped and the metadata itself if found
   private parseOutMetadata = (content: string, userThreadId: number) => {
      const metadataRegex = new RegExp(`\\*\\*\\*metadata-${userThreadId}:(.*?)\\*\\*\\*`, 's');
      const match = content.match(metadataRegex);

      if (match && match[1]) {
         const metadata = match[1].trim();
         // Remove the metadata from the content
         const cleanedContent = content.replace(metadataRegex, '').trim();
         console.info('Extracted metadata:', metadata);
         return {cleanedContent, metadata};
      }

      return {cleanedContent: content, metadata: ""};
   }


   // Validate inputs and ensure draft exists
   private async validateAndPrepareMessage(messageContent: string): Promise<{
      currentMessage: string;
      currentDraftId: number;
      threadId: number;
      updatedMessages: DecryptedContentVersionDto[];
      thread: DecryptedThread;
   } | null> {
      const { auth, threadContext } = this.props;
      const messages = threadContext.messages || [];
      let thread = threadContext.thread;

      const currentMessage = messageContent;
      if (!auth.masterKey) {
         return null;
      }
      
      // Allow empty messages only when we can ask about previous message
      if (!currentMessage.trim() && !this.canAskAboutPreviousMessage()) {
         return null;
      }

      // Ensure we have a draft ID
      let currentDraftId: number;
      if (!threadContext.currentDraftId) {
         // We may not have a thread yet either! Let's create one.
         if (!thread) {
            const { auth } = this.props;
            if (!auth.publicKey) {
               throw new Error('User public key is required to encrypt the content key');
            }
            
            thread = await threadContext.createThread("New thread" );
            
            // Update the URL to the new thread without destroying the page
            this.props.navigate(`/threads/${thread!.id}`);
            // Sidebar will auto-update from SignalR ContentCreated event
         }
         const draft = await threadContext.saveDraft(
            threadContext.currentDraftId ?? undefined, currentMessage);
         if (draft?.id) {
            currentDraftId = draft.id;
         } else {
            console.error('Failed to create draft for new message');
            return null;
         }
      } else {
         currentDraftId = threadContext.currentDraftId;
      }

      if (!thread) {
         throw new Error('Thread is not initialized');
      }

      if (!thread.id) {
         throw new Error('No thread ID set');
      }

      // Create updated messages array  
      const userMessage: DecryptedContentVersionDto = {
         id: currentDraftId,
         encryptedData: '', // Will be encrypted when sent
         messageType: 'user',
         startedAt: new Date(),
         submittedAt: undefined,
         completedAt: undefined,
         createdById: auth.user?.id || 0,
         createdByName: auth.user?.email || '',
         decryptedData: currentMessage
      };
      const updatedMessages = [...messages, userMessage];

      return {
         currentMessage,
         currentDraftId,
         threadId: thread.id,
         updatedMessages,
         thread
      };
   }

   // Create AI request callbacks
   private createAiCallbacks(threadId: number, currentDraftId: number, currentMessage: string, skipUserMessage: boolean = false) {
      const { threadContext } = this.props;
      return {
         onStart: async () => {
            if (!skipUserMessage) {
               const client = new ContentsClient();
               await client.saveDraft(threadId, {
                  draftId: currentDraftId,
                  encryptedData: await CryptoHelper.encryptContent(currentMessage, threadContext.contentKey!),
                  messageType: 'user',
                  isSubmitted: true
               });

               // User message will be updated through context, just reset AI response state
               this.setState({
                  aiResponseMessageId: null, // Will be set when first content arrives
                  aiResponseContent: '', // Reset for new response
                  aiResponseThinking: '', // Reset for new response
                  aiResponseWebSearchSources: [] // Reset for new response
               });
               this.clearDraftState();
            } else {
               // Just reset AI response state when skipping user message
               this.setState({
                  aiResponseMessageId: null,
                  aiResponseContent: '',
                  aiResponseThinking: '',
                  aiResponseWebSearchSources: []
               });
            }
         },

         onThinking: (thinkingChunk: string) => {
            this.setState(prev => ({
               aiResponseThinking: prev.aiResponseThinking + thinkingChunk
            }));
         },

         onWebSearchSources: (sources: WebSearchSource[]) => {
            this.setState(prev => ({
               aiResponseWebSearchSources: [...prev.aiResponseWebSearchSources, ...sources]
            }));
         },

         onContent: async (contentChunk: string) => {
            const newContent = this.state.aiResponseContent + contentChunk;
            this.setState({aiResponseContent: newContent});

            if (this.state.aiResponseMessageId) {
               // Update existing draft
               const encryptedData = await CryptoHelper.encryptContent(newContent, threadContext.contentKey!);
               const client = new ContentsClient();
               await client.saveDraft(threadId, {
                  draftId: this.state.aiResponseMessageId,
                  encryptedData: encryptedData,
                  messageType: 'assistant',
                  isSubmitted: false
               });
            } else if (newContent.trim()) {
               // Create AI draft on first content chunk only
               const client = new ContentsClient();
               const aiDraftResult = await client.saveDraft(threadId, {
                  encryptedData: await CryptoHelper.encryptContent(newContent, threadContext.contentKey!),
                  messageType: 'assistant',
                  isSubmitted: false
               });
               
               if (aiDraftResult?.id) {
                  this.setState({aiResponseMessageId: aiDraftResult.id});
               }
            }
         },

         onComplete: async (finalState: any) => {
            await this.handleAiComplete(finalState, threadId, currentDraftId, currentMessage, threadContext.contentKey!);
         },

         onError: (error: Error) => {
            ErrorHandler.handleError(error);
            console.error('AI request failed:', error);
            this.setState({
               activeAiResponse: null,
               aiResponseContent: '',
               aiResponseThinking: ''
            });
         }
      };
   }

   // Handle AI completion
   private async handleAiComplete(finalState: any, threadId: number, currentDraftId: number, currentMessage: string, contentKey: CryptoKey) {
      const client = new ContentsClient();

      // Complete user message
      await client.saveDraft(threadId, {
         draftId: currentDraftId,
         encryptedData: await CryptoHelper.encryptContent(currentMessage, contentKey),
         messageType: 'user',
         isSubmitted: true,
         isCompleted: true
      });

      const {aiResponseMessageId} = this.state;
      if (!aiResponseMessageId || !finalState.content) {
         throw new Error('AI response message ID or content is missing');
      }

      // Process metadata and clean content
      const {cleanedContent, metadata} = this.parseOutMetadata(finalState.content, currentDraftId);

      // Prepare encrypted attributes for web search sources and other metadata
      let encryptedAttributes: string | undefined = undefined;
      if (finalState.webSearchSources && finalState.webSearchSources.length > 0) {
         console.log('Saving web search sources to encrypted attributes:', finalState.webSearchSources);
         const attributes = {
            webSearchSources: finalState.webSearchSources
         };
         encryptedAttributes = await CryptoHelper.encryptContent(JSON.stringify(attributes), contentKey);
      }

      // Save final AI response
      const encryptedContent = await CryptoHelper.encryptContent(cleanedContent, contentKey);
      await client.saveDraft(threadId, {
         draftId: aiResponseMessageId,
         encryptedData: encryptedContent,
         encryptedAttributes: encryptedAttributes,
         messageType: 'assistant',
         isSubmitted: true,
         isCompleted: true,
         inputTokens: finalState.usage.inputTokens,
         outputTokens: finalState.usage.outputTokens,
         aiModel: 'claude-4-sonnet-20250514',
         cost: calculateCost(finalState.usage.inputTokens, finalState.usage.outputTokens)
      });

      // Handle metadata (title updates, etc.)
      const { threadContext } = this.props;
      if (threadContext.thread) {
         await this.processMetadata(threadContext.thread, metadata);
      }

      // Update the cached thread data with web search sources
      if (finalState.webSearchSources && finalState.webSearchSources.length > 0 && aiResponseMessageId) {
         const { queryClient } = this.props;
         const threadId = this.props.id;
         
         queryClient.setQueryData(['thread', threadId], (oldData: DecryptedThread | undefined) => {
            if (!oldData) return oldData;
            
            const updatedMessages = oldData.decryptedMessages?.map(msg => 
               msg.id === aiResponseMessageId 
                  ? {
                      ...msg,
                      decryptedAttributes: {
                         webSearchSources: finalState.webSearchSources
                      }
                    }
                  : msg
            );
            
            return {
               ...oldData,
               decryptedMessages: updatedMessages
            };
         });
      }

      // Context will handle adding the assistant message through TanStack Query
      this.setState({
         activeAiResponse: null,
         aiResponseContent: '',
         aiResponseThinking: '',
         aiResponseWebSearchSources: []
      });
   }

   // Process metadata from AI response
   private async processMetadata(thread: ContentDetailsDto, metadata: string) {
      if (!metadata) return;
      if (!thread) {
         throw new Error('No thread set processing meta data in AI response');
      }
      
      if (!thread.id) {
         throw new Error('Thread ID is required to update metadata');
      }

      try {
         const metadataObject = JSON.parse(metadata);
         if (metadataObject.betterTitle) {
            // encrypt the new title
            const { threadContext } = this.props;
            const encryptedTitle = await CryptoHelper.encryptContent(metadataObject.betterTitle, threadContext.contentKey!);
            await threadContext.updateTitle(encryptedTitle);
         }
      } catch (error) {
         console.error('Failed to parse AI metadata ' + metadata, error);
      }
   }

   // Main send message handler
   private handleSendMessage = async () => {
      this.setState({sending: true});
      try {
         // If input is empty and we can ask about previous, always use AI handler
         const submitToAi = this.props.threadContext.isAiMode || (this.state.newMessage.trim() === '' && this.canAskAboutPreviousMessage());
         
         this.scrollToBottom();
         
         const { threadContext } = this.props;
         
         // Check if we need to ask about previous message (empty input)
         let messageContent = this.state.newMessage.trim();
         let shouldCreateUserMessage = true;
         
         if (!messageContent && this.canAskAboutPreviousMessage()) {
            const messages = threadContext.messages || [];
            const lastUserMessage = messages.filter(msg => msg.messageType === 'user').pop();
            if (lastUserMessage) {
               messageContent = lastUserMessage.decryptedData || '';
               shouldCreateUserMessage = false; // Don't create a new user message, just respond to the previous one
            }
         }
         
         const prepared = await this.validateAndPrepareMessage(messageContent);
         console.log(prepared, threadContext);
         if (!prepared) return;
         
         if (!submitToAi && shouldCreateUserMessage) {
            const client = new ContentsClient();
            await client.saveDraft(prepared.threadId, {
               draftId: prepared.currentDraftId,
               encryptedData: await CryptoHelper.encryptContent(prepared.currentMessage, prepared.thread.contentKey!),
               messageType: 'user',
               isSubmitted: true
            });
            this.clearDraftState();
            return;
         }
   
         // Get API key
         const claudeClient = new ClaudeClient();
         const {apiKey} = await claudeClient.getApiKey();
         if (!apiKey) {
            throw new Error('API key is required to send messages');
         }
      
         // Clean up any previous AI response
         this.state.activeAiResponse?.destroy();
         this.setState({
            aiResponseContent: '',
            aiResponseThinking: '',
            aiResponseWebSearchSources: []
         });

         // Create new AI request
         const aiRequest = new AiRequest({
            requestThreadId: prepared.threadId,
            messages: prepared.updatedMessages,
            apiKey: apiKey,
            system: `
End every response with json metadata in this format:
***metadata-${prepared.currentDraftId}:{fields}***
Include these fields in the metadata:
betterTitle: A 5-ish word suggested title for the full thread, ONLY include this field if your suggestion is meaningfully better than ("${threadContext.decryptedTitle}")
`,
            callbacks: this.createAiCallbacks(
               prepared.threadId,
               prepared.currentDraftId,
               prepared.currentMessage,
               !shouldCreateUserMessage
            ),
            // Thread-specific settings
            enableThinking: threadContext.enableThinking,
            enableWebSearch: threadContext.enableWebSearch
         });

         this.setState({activeAiResponse: aiRequest});

      } finally {
         this.setState({sending: false});
      }
   }

   // This gets assigned by the ScrollController component inside the scrollable container
   private scrollToBottom: (() => void) = () => {};
   render() {
      const {
         newMessage, sending,
         activeAiResponse, aiResponseThinking, aiResponseContent
      } = this.state;
      
      // Get data from context instead of state
      const { threadContext } = this.props;
      const messages = threadContext.messages || [];
      const decryptedTitle = threadContext.decryptedTitle || '';
      const loading = threadContext.isLoading;

      if (loading) {
         return <div className="text-center p-5">Loading thread...</div>;
      }


      const { showPermissions } = this.state;
      const thread = threadContext.thread;

      if (showPermissions && thread?.id && threadContext.contentKey) {
         return (
            <ThreadPermissions
               threadId={thread.id}
               threadTitle={decryptedTitle || 'Thread'}
               contentKey={threadContext.contentKey}
               onClose={() => this.setState({ showPermissions: false })}
            />
         );
      }

      return (
         <div className="d-flex flex-column flex-grow-1 h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
               {decryptedTitle ? <h1>{decryptedTitle}</h1> : <>&nbsp;</>}
               <div>
                  {thread?.id && (
                     <UncontrolledButtonDropdown className="me-2">
                        <button 
                           className="btn btn-outline-primary"
                           onClick={() => this.setState({ showPermissions: true })}
                        >
                           <i className="fas fa-users me-2"></i>
                           Manage Permissions
                        </button>
                        <DropdownToggle split color="outline-primary" />
                        <DropdownMenu>
                           <DropdownItem onClick={() => renameThreadWithConfirmation(thread)}>Rename Thread...</DropdownItem>
                           <DropdownItem divider />
                           <DropdownItem className="text-danger" onClick={() => deleteThreadWithConfirmation(thread)}>Delete Thread...</DropdownItem>
                        </DropdownMenu>
                     </UncontrolledButtonDropdown>
                  )}
                  <Link className="btn btn-outline-secondary" to="/">
                     Back to Dashboard
                  </Link>
               </div>
            </div>

            <ScrollToBottom className="px-3 py-1 flex-grow-1 mb-2 border rounded overflow-auto"
               initialScrollBehavior={'auto'}
            >
               <ScrollController 
                  getScrollFn={scrollFunc => { this.scrollToBottom = scrollFunc;}}
               />
               
               {messages.length === 0 ? (
                  <div className="text-center text-muted p-5">
                     <p>Start the conversation by sending a message.</p>
                  </div>
               ) : (
                  <>
                     {messages.map((message) => (
                        <MessageComponent
                           key={message.id}
                           message={message}
                           currentUserId={this.props.auth.user?.id}
                           onDelete={this.handleDeleteMessage}
                        />
                     ))}

                     {/* Show AI response in progress */}
                     {activeAiResponse && (aiResponseThinking || aiResponseContent) && (
                        <MessageComponent
                           isStreaming={true}
                           streamingContent={aiResponseContent}
                           streamingThinking={aiResponseThinking}
                           streamingWebSearchSources={this.state.aiResponseWebSearchSources}
                           currentUserId={this.props.auth.user?.id}
                        />
                     )}

                  </>
               )}

               {/* Typing indicators at bottom of messages area */}
               {this.state.typingUsers.length > 0 && (
                  <div className="d-flex align-items-center text-muted small">
                     <div className="me-2">
                        <div className="typing-indicator">
                           <div className="typing-dot"></div>
                           <div className="typing-dot"></div>
                           <div className="typing-dot"></div>
                        </div>
                     </div>
                     <span>
                     {this.state.typingUsers.length === 1
                        ? `${this.state.typingUsers[0].userName} is typing...`
                        : `${this.state.typingUsers.length} users are typing...`
                     }
                  </span>
                  </div>
               )}
            </ScrollToBottom>

            <div className="mt-auto">
               {/* AI Settings Status Bar */}
               <div className="d-flex justify-content-between align-items-center mb-1 px-2">
                  <div className="d-flex gap-3">
                     <ThreadContext.Consumer>
                        {(threadContext) => (
                           <>
                              <div className="form-check form-switch d-flex align-items-center">
                                 <input
                                    className="form-check-input me-1"
                                    type="checkbox"
                                    id="thinking-toggle-bar"
                                    checked={threadContext?.enableThinking || false}
                                    onChange={(e) => threadContext?.setThinkingEnabled(e.target.checked)}
                                    disabled={sending || !!activeAiResponse}
                                 />
                                 <label className="form-check-label small text-muted" htmlFor="thinking-toggle-bar">
                                    <i className="fas fa-brain me-1"></i>
                                    Thinking
                                 </label>
                              </div>
                              
                              <div className="form-check form-switch d-flex align-items-center">
                                 <input
                                    className="form-check-input me-1"
                                    type="checkbox"
                                    id="web-search-toggle-bar"
                                    checked={threadContext?.enableWebSearch || false}
                                    onChange={(e) => threadContext?.setWebSearchEnabled(e.target.checked)}
                                    disabled={sending || !!activeAiResponse}
                                 />
                                 <label className="form-check-label small text-muted" htmlFor="web-search-toggle-bar">
                                    <i className="fas fa-search me-1"></i>
                                    Web Search
                                 </label>
                              </div>
                           </>
                        )}
                     </ThreadContext.Consumer>
                  </div>
                  
                  {/* Ask AI toggle - right aligned */}
                  {!(newMessage.trim() === '' && this.canAskAboutPreviousMessage()) && (
                     <ThreadContext.Consumer>
                        {(threadContext) => (
                           <div className="form-check form-switch d-flex align-items-center">
                              <input
                                 className="form-check-input me-1"
                                 type="checkbox"
                                 id="aiModeToggleBar"
                                 checked={threadContext?.isAiMode || false}
                                 onChange={(e) => threadContext?.setAiModeEnabled(e.target.checked)}
                                 disabled={sending || !!activeAiResponse}
                              />
                              <label className="form-check-label small text-muted" htmlFor="aiModeToggleBar">
                                 <i className="fas fa-robot me-1"></i>
                                 Ask AI
                              </label>
                           </div>
                        )}
                     </ThreadContext.Consumer>
                  )}
               </div>
               
               <div className="input-group">
                   <textarea
                      ref={this.textareaRef}
                      className="form-control"
                      placeholder={this.getPlaceholderText()}
                      defaultValue={newMessage}
                      onChange={(e) => this.handleMessageChange(e.target.value)}
                      disabled={sending}
                      rows={3}
                      onKeyDown={async (e) => {
                         if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            await this.handleSendMessage();
                         }
                      }}
                   />
                  <div className="input-group-append d-flex">
                     {/* Send button */}
                     <button
                        className="btn btn-primary h-100"
                        onClick={this.handleSendMessage}
                        disabled={sending || !!activeAiResponse || !this.canSendMessage()}
                     >
                        {sending ? 'Sending...' : 
                         activeAiResponse ? 'AI Responding...' : 
                         newMessage.trim() === '' && this.canAskAboutPreviousMessage() ? 'Ask AI' :
                         threadContext.isAiMode ? 'Ask AI' : 'Send'}
                     </button>
                  </div>
               </div>
               <div className="form-text text-end">
                  Press Enter to {threadContext.isAiMode ? 'ask AI' : 'send message'}, Shift+Enter for new line
               </div>
            </div>
         </div>
      );
   }
}

// Wrapper component to provide router hooks and auth context
const ThreadDetailWrapper = () => {
   const string_id = useParams<{ id: string }>().id;
   const navigate = useNavigate();
   const auth = useAuth();
   const queryClient = useQueryClient();

   const id = (!string_id || string_id === "new") ? undefined : parseInt(string_id);

   return (
      <ThreadProvider threadId={id}>
         <ThreadContext.Consumer>
            {(threadContext) => (
               <ThreadDetail 
                  id={id}
                  navigate={navigate} 
                  auth={auth} 
                  threadContext={threadContext!}
                  queryClient={queryClient}
               />
            )}
         </ThreadContext.Consumer>
      </ThreadProvider>
   );
};

export default ThreadDetailWrapper;